import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import {
  FaHome,
  FaHandshake,
  FaRegQuestionCircle,
  FaClipboardList,
} from "react-icons/fa";
import { GrSchedule } from "react-icons/gr";
import { BsPeopleFill } from "react-icons/bs";

const Navbar = ({ scrollToSection, activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [position, setPosition] = useState({ left: 0, width: 0, opacity: 0 });
  const [nav, setNav] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    // Get responsive scroll threshold based on screen size
    const getScrollThreshold = () => {
      return window.innerWidth < 768 ? 10 : 50; // 10px for mobile, 50px for desktop
    };

    // Check screen size for logo selection
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 379);
    };

    // Check initial scroll position and screen size on mount
    setIsScrolled(window.scrollY > getScrollThreshold());
    checkScreenSize();

    const handleScroll = () => {
      setIsScrolled(window.scrollY > getScrollThreshold());
    };

    const handleResize = () => {
      checkScreenSize();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "schedule", label: "Schedule" },
    { id: "speakers", label: "Presenters" },
    { id: "rubric", label: "Rules & Prizes" },
    { id: "sponsors", label: "Sponsors" },
    { id: "faq", label: "FAQ" },
  ];

  const handleClick = () => {
    scrollToSection("home");
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-navbar dark:glass-navbar-dark" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile & Desktop Left Side */}
          <div className="flex items-center">
            {/* Mobile Hamburger Menu */}
            <div
              onClick={() => setNav(!nav)}
              className="lg:hidden cursor-pointer mr-4"
            >
              <AiOutlineMenu
                size={24}
                className="text-gray-700 dark:text-white"
              />
            </div>

            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center"
              onClick={handleClick}
            >
              <div className="flex-shrink-0">
                <div className="flex items-center">
                  <img
                    src="/buildathon_logo.png"
                    alt="Buildathon"
                    className={isSmallScreen ? "h-6 w-auto" : "h-8 w-auto"}
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Navigation Links - Desktop Only */}
          <div className="hidden lg:block">
            <ul
              onMouseLeave={() => {
                setPosition((pv) => ({
                  ...pv,
                  opacity: 0,
                }));
              }}
              className="relative mx-auto flex w-fit rounded-full p-1"
            >
              {navItems.map((item) => (
                <NavTab
                  key={item.id}
                  setPosition={setPosition}
                  onClick={() => scrollToSection(item.id)}
                  isActive={activeSection === item.id}
                >
                  {item.label}
                </NavTab>
              ))}
              <Cursor position={position} />
            </ul>
          </div>

          {/* Livestream Button */}
          <div className="flex items-center">
            <motion.a
              href="https://www.youtube.com/live/Brz7GaUPEDw"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-aifund text-white px-4 md:px-6 py-2 rounded-full font-medium hover:bg-aifund-dark transition-colors duration-200 shadow-lg hover:shadow-xl inline-block text-sm md:text-base"
            >
              <span className="max-[325px]:hidden">LIVESTREAM</span>
              <span className="hidden max-[325px]:inline">LIVE</span>
            </motion.a>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {nav && (
        <div
          className="bg-black/80 fixed w-full h-screen z-40 top-0 left-0 lg:hidden"
          onClick={() => setNav(false)}
        ></div>
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed top-0 left-0 w-[300px] h-screen bg-white dark:bg-gray-900 z-50 duration-300 lg:hidden ${
          nav ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Close Button */}
        <AiOutlineClose
          onClick={() => setNav(!nav)}
          size={24}
          className="absolute top-4 right-4 cursor-pointer text-gray-700 dark:text-white"
        />

        {/* Menu Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <img
              src="/buildathon_logo.png"
              alt="Buildathon"
              className={isSmallScreen ? "h-6 w-auto" : "h-8 w-auto"}
            />
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="mt-4">
          <ul className="flex flex-col p-4 text-gray-800 dark:text-gray-200">
            {navItems.map((item) => (
              <li
                key={item.id}
                className="py-3 border-b border-gray-100 dark:border-gray-800"
              >
                <button
                  onClick={() => {
                    scrollToSection(item.id);
                    setNav(false);
                  }}
                  className="flex items-center w-full text-left text-lg hover:text-aifund transition-colors duration-200"
                >
                  <span className="mr-3">
                    {item.id === "home" && <FaHome size={18} />}
                    {item.id === "schedule" && <GrSchedule size={18} />}
                    {item.id === "speakers" && <BsPeopleFill size={18} />}
                    {item.id === "rubric" && <FaClipboardList size={18} />}
                    {item.id === "sponsors" && <FaHandshake size={18} />}
                    {item.id === "faq" && <FaRegQuestionCircle size={18} />}
                  </span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </motion.nav>
  );
};

const NavTab = ({ children, setPosition, onClick, isActive }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (isActive && ref.current) {
      const { width } = ref.current.getBoundingClientRect();
      setPosition({
        left: ref.current.offsetLeft,
        width,
        opacity: 1,
      });
    }
  }, [isActive, setPosition]);

  return (
    <li
      ref={ref}
      onMouseEnter={() => {
        if (!ref?.current) return;

        const { width } = ref.current.getBoundingClientRect();

        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
      }}
      onClick={onClick}
      className="relative z-10 block cursor-pointer px-3 py-1.5 text-xs uppercase text-gray-700 dark:text-white md:px-5 md:py-3 md:text-sm font-medium transition-colors duration-200 hover:text-aifund dark:hover:text-aifund"
    >
      {children}
    </li>
  );
};

const Cursor = ({ position }) => {
  return (
    <motion.li
      animate={{
        ...position,
      }}
      className="absolute z-0 bottom-0 h-0.5 bg-aifund transition-colors duration-300"
    />
  );
};

export default Navbar;
